import dayjs from 'dayjs';
import { TextToWinTemplateSettings, TemplateFieldControl } from '../types';

/**
 * Fields the backend mandates for text-to-win campaigns. Their Visible/Required
 * toggles are hidden in the config builder and forced on by normalize().
 */
export const REQUIRED_TEXT_TO_WIN_TEMPLATE_FIELDS: (keyof TextToWinTemplateSettings)[] = [
  'dateStart',
  'dateEnd',
  'dateDraw',
  'dateResultsSend',
  'entryIntervalMinutes',
  'maxEntriesPerInterval',
  'maxEntriesPerContact',
  'winnersCount',
  'entrySuccessMessageText',
  'winnerMessageText',
];

const control = <T>(value: T, overrides?: Partial<TemplateFieldControl<T>>): TemplateFieldControl<T> => ({
  visible: true,
  required: false,
  default: value,
  ...overrides,
});

/**
 * Default template-level text-to-win settings.
 * Defaults mirror the runtime createDefaultTextToWinSettings() shape.
 */
export const createDefaultTextToWinTemplateSettings = (): TextToWinTemplateSettings => ({
  visible: true,
  dateStart: control<string>('', { required: true }),
  dateEnd: control<string>('', { required: true }),
  dateDraw: control<string>('', { required: true }),
  dateResultsSend: control<string>('', { required: true }),
  entryIntervalMinutes: control<number | null>(null, { required: true }),
  maxEntriesPerInterval: control<number | null>(null, { required: true }),
  maxEntriesPerContact: control<number | null>(null, { required: true }),
  winnersCount: control<number | null>(null, { required: true }),
  entrySuccessMessageText: control<string>('', { required: true }),
  winnerMessageText: control<string>('', { required: true }),
  entryIntervalLimitMessageText: control<string | null>(null),
  entryTotalLimitMessageText: control<string | null>(null),
  entryNotStartedMessageText: control<string | null>(null),
  entryEndedMessageText: control<string | null>(null),
  nonWinnerMessageText: control<string | null>(null),
});

const normalizeControl = <T>(
  raw: Partial<TemplateFieldControl<T>> | undefined,
  fallback: TemplateFieldControl<T>,
  forceOn = false,
): TemplateFieldControl<T> => ({
  visible: forceOn ? true : typeof raw?.visible === 'boolean' ? raw.visible : fallback.visible,
  required: forceOn ? true : typeof raw?.required === 'boolean' ? raw.required : fallback.required,
  default: raw?.default !== undefined ? (raw.default as T) : fallback.default,
  label: raw?.label ?? fallback.label,
});

const isRequiredField = (key: keyof TextToWinTemplateSettings) =>
  REQUIRED_TEXT_TO_WIN_TEMPLATE_FIELDS.includes(key);

/**
 * Coerce a partial / loaded text-to-win settings block into the full shape.
 * Backend-mandated fields are forced visible + required regardless of stored
 * value, so a stale/old config can never hide a field the backend needs.
 */
export const normalizeTextToWinTemplateSettings = (
  settings?: Partial<TextToWinTemplateSettings> | null,
): TextToWinTemplateSettings => {
  const defaults = createDefaultTextToWinTemplateSettings();
  const norm = <K extends keyof Omit<TextToWinTemplateSettings, 'visible'>>(key: K) =>
    normalizeControl(settings?.[key] as never, defaults[key] as never, isRequiredField(key)) as TextToWinTemplateSettings[K];
  return {
    visible: typeof settings?.visible === 'boolean' ? settings.visible : defaults.visible,
    dateStart: norm('dateStart'),
    dateEnd: norm('dateEnd'),
    dateDraw: norm('dateDraw'),
    dateResultsSend: norm('dateResultsSend'),
    entryIntervalMinutes: norm('entryIntervalMinutes'),
    maxEntriesPerInterval: norm('maxEntriesPerInterval'),
    maxEntriesPerContact: norm('maxEntriesPerContact'),
    winnersCount: norm('winnersCount'),
    entrySuccessMessageText: norm('entrySuccessMessageText'),
    winnerMessageText: norm('winnerMessageText'),
    entryIntervalLimitMessageText: norm('entryIntervalLimitMessageText'),
    entryTotalLimitMessageText: norm('entryTotalLimitMessageText'),
    entryNotStartedMessageText: norm('entryNotStartedMessageText'),
    entryEndedMessageText: norm('entryEndedMessageText'),
    nonWinnerMessageText: norm('nonWinnerMessageText'),
  };
};

/**
 * Validate the template's default date values for correct chronological order.
 * Required-presence of runtime values is enforced at campaign creation time.
 */
export const validateTextToWinTemplateSettings = (
  settings?: TextToWinTemplateSettings | null,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!settings) return errors;

  const start = settings.dateStart.default ? dayjs(settings.dateStart.default) : null;
  const end = settings.dateEnd.default ? dayjs(settings.dateEnd.default) : null;
  const draw = settings.dateDraw.default ? dayjs(settings.dateDraw.default) : null;
  const resultsSend = settings.dateResultsSend.default ? dayjs(settings.dateResultsSend.default) : null;

  if (start?.isValid() && end?.isValid() && !end.isAfter(start)) {
    errors['textToWinSettings.dateEnd'] = 'End date must be after the start date.';
  }
  if (end?.isValid() && draw?.isValid() && !draw.isAfter(end)) {
    errors['textToWinSettings.dateDraw'] = 'Draw date must be after the end date.';
  }
  if (draw?.isValid() && resultsSend?.isValid() && !resultsSend.isAfter(draw)) {
    errors['textToWinSettings.dateResultsSend'] = 'Results send date must be after the draw date.';
  }

  return errors;
};
